import {
  PacketTypes,
  PlayerMove,
  Attack,
  BoardState,
  PlayerStats,
} from "./types";

// Base packet class
export abstract class Packet {
  packetId: PacketTypes;
  // ID of the connection that sent this packet (set by network layer)
  senderId: string = "";

  constructor(id: PacketTypes) {
    this.packetId = id;
  }

  abstract serialize(): any;
  abstract deserialize(data: any): void;
}

/**
 * Packet for initial join request (matches original P_WANTJOIN)
 * Reference: quadra/source/packets.h:97-116 (Packet_wantjoin)
 * Sent first to request game info, server responds with P_GAMESERVER
 */
export class WantJoinPacket extends Packet {
  /** Network protocol version (matches original net_version uint8_t) */
  netVersion: number = 23;
  /** Language code (matches original language uint8_t) */
  language: number = 0;
  /** Operating system (matches original os uint8_t, 1=Windows, 2=Unix) */
  os: number = 2;

  constructor() {
    super(PacketTypes.WANT_JOIN);
  }

  serialize(): any {
    return {
      netVersion: this.netVersion,
      language: this.language,
      os: this.os,
    };
  }

  deserialize(data: any): void {
    this.netVersion = data.netVersion || 23;
    this.language = data.language || 0;
    this.os = data.os !== undefined ? data.os : 2;
  }
}

/**
 * Packet for player join request (matches original P_PLAYERWANTJOIN)
 * Reference: quadra/source/packets.h:216-232 (Packet_playerwantjoin)
 * Sent after receiving P_GAMESERVER, contains player details
 */
export class PlayerWantJoinPacket extends Packet {
  /** Player's display name (matches original name[40]) */
  playerName: string = "";
  /** Preferred team number (0-8) (matches original team uint8_t) */
  playerTeam: number = 0;
  /** Player slot number (0-5) (matches original player uint8_t) */
  player: number = 0;
  /** Horizontal repeat setting (matches original h_repeat int) */
  hRepeat: number = 2;
  /** Vertical repeat setting (matches original v_repeat int) */
  vRepeat: number = 2;
  /** Smooth rendering (matches original smooth int, 0 or 1) */
  smooth: number = 1;
  /** Shadow rendering (matches original shadow int, 0 or 1) */
  shadow: number = 1;
  /** Handicap level requested (0 = none) (matches original handicap int) */
  handicap: number = 0;
  /** Persistent player hash for rejoin detection (matches original player_hash[16]) */
  playerHash: string = "";
  /** Team name (matches original team_name[40]) */
  teamName: string = "";
  /** Team hash (matches original team_hash[16]) */
  teamHash: string = "";
  /** Preferred block pack (custom extension) */
  blockPack?: string;
  /** Preferred canvas pack (custom extension) */
  canvasPack?: string;

  constructor() {
    super(PacketTypes.JOIN_REQUEST);
  }

  serialize(): any {
    return {
      playerName: this.playerName,
      playerTeam: this.playerTeam,
      player: this.player,
      hRepeat: this.hRepeat,
      vRepeat: this.vRepeat,
      smooth: this.smooth,
      shadow: this.shadow,
      handicap: this.handicap,
      playerHash: this.playerHash,
      teamName: this.teamName,
      teamHash: this.teamHash,
      ...(this.blockPack !== undefined && { blockPack: this.blockPack }),
      ...(this.canvasPack !== undefined && { canvasPack: this.canvasPack }),
    };
  }

  deserialize(data: any): void {
    this.playerName = data.playerName || "";
    this.playerTeam = data.playerTeam || 0;
    this.player = data.player !== undefined ? data.player : 0;
    this.hRepeat = data.hRepeat !== undefined ? data.hRepeat : 2;
    this.vRepeat = data.vRepeat !== undefined ? data.vRepeat : 2;
    this.smooth = data.smooth !== undefined ? data.smooth : 1;
    this.shadow = data.shadow !== undefined ? data.shadow : 1;
    this.handicap = data.handicap || 0;
    this.playerHash = data.playerHash || "";
    this.teamName = data.teamName || "";
    this.teamHash = data.teamHash || "";
    this.blockPack = data.blockPack;
    this.canvasPack = data.canvasPack;
  }
}

/**
 * Packet for responding to join requests
 * Matches original Packet_playeraccepted (packets.h:253-268)
 * accepted values: 0=accepted, 1=server refuses, 2=already in game, 3=game finished, 4=rejoin, 5=full
 */
export class JoinResponsePacket extends Packet {
  /** Whether the join was accepted (legacy boolean, use acceptedCode for original behavior) */
  accepted: boolean = false;
  /** Acceptance code matching original (0=accepted, 1=refused, 2=already in game, 3=finished, 4=rejoin, 5=full) */
  acceptedCode: number = 0;
  /** Assigned connection ID */
  connectionId: string = "";
  /** Host connection ID */
  hostConnectionId: string = "";
  /** Server-assigned player ID for rejoin detection (matches original player_id, uint32_t) */
  playerId?: number;
  /** Position/slot number for rejoining player */
  rejoinPosition?: number;
  /** Whether this is a rejoin (acceptedCode=4) */
  isRejoin: boolean = false;
  /** Preserved stats if rejoining (same team) */
  preservedStats?: {
    score: number;
    frags: number;
    deaths: number;
    lines: number;
    level: number;
  };
  /** Reason for rejection if not accepted */
  reason?: string;
  /** Game seed for random number generation */
  gameSeed: number = 0;
  /** Current game parameters */
  gameParams?: {
    gameMode: string;
    levelUp: boolean;
    startLevel: number;
    comboMin: number;
    allowHandicap: boolean;
    gameEndType: "none" | "frag" | "lines" | "time" | "score";
    gameEndValue: number;
  };

  constructor() {
    super(PacketTypes.JOIN_RESPONSE);
  }

  serialize(): any {
    return {
      accepted: this.accepted,
      acceptedCode: this.acceptedCode,
      connectionId: this.connectionId,
      hostConnectionId: this.hostConnectionId,
      playerId: this.playerId,
      rejoinPosition: this.rejoinPosition,
      isRejoin: this.isRejoin,
      preservedStats: this.preservedStats,
      reason: this.reason,
      gameSeed: this.gameSeed,
      gameParams: this.gameParams,
    };
  }

  deserialize(data: any): void {
    this.accepted =
      data.accepted !== undefined
        ? data.accepted
        : data.acceptedCode === 0 || data.acceptedCode === 4;
    this.acceptedCode =
      data.acceptedCode !== undefined
        ? data.acceptedCode
        : data.accepted
        ? 0
        : 1;
    this.connectionId = data.connectionId || data.playerId || ""; // Backward compatibility
    this.hostConnectionId = data.hostConnectionId || "";
    this.playerId = data.playerId !== undefined && typeof data.playerId === 'number' ? data.playerId : undefined;
    this.rejoinPosition = data.rejoinPosition;
    this.isRejoin = data.isRejoin || this.acceptedCode === 4;
    this.preservedStats = data.preservedStats;
    this.reason = data.reason;
    this.gameSeed = data.gameSeed;
    this.gameParams = data.gameParams;
  }
}

/**
 * Packet for initial game server info sent on join (matches original P_GAMESERVER)
 * Reference: quadra/source/packets.h:162-193 (Packet_gameserver)
 * Sent once when player joins, contains full game configuration and player list
 */
export class GameserverPacket extends Packet {
  /** Network protocol version */
  version: number = 0;
  /** All players in the game */
  players: Array<{
    quel: number; // Player slot (0-5)
    team: number; // Team number
    name: string; // Player name
    playerId: number; // Server-assigned player ID
    handicap: number; // Handicap level
    blockPack?: string; // Block pack preference
    canvasPack?: string; // Canvas pack preference
    fragAnnouncerPack?: string; // Frag announcer pack preference
  }> = [];
  /** Game name */
  name: string = "";
  /** Whether join was accepted (legacy, always true if packet is sent) */
  accepted: boolean = true;
  /** Random seed for game events */
  gameSeed: number = 0;
  /** Game end value (for time-based games) */
  gameEndValue: number = 0;
  /** Whether the game is paused */
  paused: boolean = false;
  /** Whether level up is disabled */
  noLevelUp: boolean = false;
  /** Starting level */
  levelStart: number = 1;
  /** Combo minimum */
  comboMin: number = 2;
  /** Whether handicaps are allowed */
  allowHandicap: boolean = true;
  /** Whether survivor mode */
  survivor: boolean = false;
  /** Whether hot potato mode */
  hotPotato: boolean = false;
  /** Normal attack configuration */
  normalAttack?: Attack;
  /** Clean attack configuration */
  cleanAttack?: Attack;
  /** Potato normal attack configuration */
  potatoNormalAttack?: Attack;
  /** Potato clean attack configuration */
  potatoCleanAttack?: Attack;
  /** Game end type */
  gameEnd: number = 0;
  /** Delay start (frames) */
  delayStart: number = 0;
  /** Whether server wants move packets */
  wantsMoves: boolean = false;
  /** Current syncpoint state (matches original Canvas::State) */
  syncpoint: "playing" | "waitForWinner" | "waitForRestart" | "last" = "playing";
  /** Potato team */
  potatoTeam: number = 255;
  /** Whether single player mode */
  single: boolean = false;
  /** Whether game is terminated */
  terminated: boolean = false;
  /** Whether boring rules (legacy) */
  boringRules: boolean = false;
  /** Host connection ID */
  hostConnectionId: string = "";

  constructor() {
    super(PacketTypes.GAMESERVER);
  }

  serialize(): any {
    return {
      version: this.version,
      players: this.players,
      name: this.name,
      accepted: this.accepted,
      gameSeed: this.gameSeed,
      gameEndValue: this.gameEndValue,
      paused: this.paused,
      noLevelUp: this.noLevelUp,
      levelStart: this.levelStart,
      comboMin: this.comboMin,
      allowHandicap: this.allowHandicap,
      survivor: this.survivor,
      hotPotato: this.hotPotato,
      normalAttack: this.normalAttack,
      cleanAttack: this.cleanAttack,
      potatoNormalAttack: this.potatoNormalAttack,
      potatoCleanAttack: this.potatoCleanAttack,
      gameEnd: this.gameEnd,
      delayStart: this.delayStart,
      wantsMoves: this.wantsMoves,
      syncpoint: this.syncpoint,
      potatoTeam: this.potatoTeam,
      single: this.single,
      terminated: this.terminated,
      boringRules: this.boringRules,
      hostConnectionId: this.hostConnectionId,
    };
  }

  deserialize(data: any): void {
    this.version = data.version || 0;
    this.players = data.players || [];
    this.name = data.name || "";
    this.accepted = data.accepted !== undefined ? data.accepted : true;
    this.gameSeed = data.gameSeed || 0;
    this.gameEndValue = data.gameEndValue || 0;
    this.paused = data.paused || false;
    this.noLevelUp = data.noLevelUp || false;
    this.levelStart = data.levelStart || 1;
    this.comboMin = data.comboMin || 2;
    this.allowHandicap = data.allowHandicap !== undefined ? data.allowHandicap : true;
    this.survivor = data.survivor || false;
    this.hotPotato = data.hotPotato || false;
    this.normalAttack = data.normalAttack;
    this.cleanAttack = data.cleanAttack;
    this.potatoNormalAttack = data.potatoNormalAttack;
    this.potatoCleanAttack = data.potatoCleanAttack;
    this.gameEnd = data.gameEnd || 0;
    this.delayStart = data.delayStart || 0;
    this.wantsMoves = data.wantsMoves || false;
    this.syncpoint = data.syncpoint || "playing";
    this.potatoTeam = data.potatoTeam !== undefined ? data.potatoTeam : 255;
    this.single = data.single || false;
    this.terminated = data.terminated || false;
    this.boringRules = data.boringRules || false;
    this.hostConnectionId = data.hostConnectionId || "";
  }
}

/**
 * Packet for notifying existing clients about a new player (matches original P_PLAYER)
 * Reference: quadra/source/packets.h:234-251 (Packet_player)
 * Sent to all existing clients when a new player joins (dispatched via net->dispatch)
 * Original: net_server.cc:440 (net->dispatch(&player, P_PLAYER, p->from))
 */
export class PlayerPacket extends Packet {
  /** Team number (0-8) */
  team: number = 0;
  /** Player name */
  name: string = "";
  /** Server-assigned player ID (matches original player_id, uint32_t) */
  playerId: number = 0;
  /** Player slot number (0-5) (matches original player, uint8_t) */
  player: number = 0;
  /** Horizontal repeat setting (matches original h_repeat, int) */
  hRepeat: number = 2;
  /** Vertical repeat setting (matches original v_repeat, int) */
  vRepeat: number = 2;
  /** Smooth rendering (matches original smooth, int 0 or 1) */
  smooth: number = 1;
  /** Shadow rendering (matches original shadow, int 0 or 1) */
  shadow: number = 1;
  /** Player position/slot (0-5) (matches original pos, uint8_t) */
  pos: number = 0;
  /** Handicap level (matches original handicap, int) */
  handicap: number = 0;
  /** Block pack preference (matches PlayerState.blockPack) */
  blockPack?: string;
  /** Canvas pack preference (matches PlayerState.canvasPack) */
  canvasPack?: string;
  /** Frag announcer pack preference (matches PlayerState.fragAnnouncerPack) */
  fragAnnouncerPack?: string;

  constructor() {
    super(PacketTypes.PLAYER);
  }

  serialize(): any {
    return {
      team: this.team,
      name: this.name,
      playerId: this.playerId,
      player: this.player,
      hRepeat: this.hRepeat,
      vRepeat: this.vRepeat,
      smooth: this.smooth,
      shadow: this.shadow,
      pos: this.pos,
      handicap: this.handicap,
      blockPack: this.blockPack,
      canvasPack: this.canvasPack,
      fragAnnouncerPack: this.fragAnnouncerPack,
    };
  }

  deserialize(data: any): void {
    this.team = data.team || 0;
    this.name = data.name || "";
    this.playerId = data.playerId || 0;
    this.player = data.player !== undefined ? data.player : 0;
    this.hRepeat = data.hRepeat !== undefined ? data.hRepeat : 2;
    this.vRepeat = data.vRepeat !== undefined ? data.vRepeat : 2;
    this.smooth = data.smooth !== undefined ? data.smooth : 1;
    this.shadow = data.shadow !== undefined ? data.shadow : 1;
    this.pos = data.pos !== undefined ? data.pos : 0;
    this.handicap = data.handicap || 0;
    this.blockPack = data.blockPack;
    this.canvasPack = data.canvasPack;
    this.fragAnnouncerPack = data.fragAnnouncerPack;
  }
}

/**
 * Packet for server syncpoint state updates (matches original P_SERVERSTATE)
 * Reference: quadra/source/packets.h:591-598 (Packet_serverstate)
 * Sent periodically to broadcast syncpoint changes (Canvas::State transitions)
 * Extended to include countdown for synchronization (original doesn't broadcast countdown)
 */
export class ServerStatePacket extends Packet {
  /** Syncpoint state (matches original Canvas::State enum) */
  state: "playing" | "waitForWinner" | "waitForRestart" | "last" = "playing";
  /** Countdown value (optional, for countdown synchronization) */
  countdown?: number;
  /** Game messages for announcer events */
  gameMessages?: import("./types").GameMessageEvent[];

  constructor() {
    super(PacketTypes.SERVER_STATE);
  }

  serialize(): any {
    return {
      state: this.state,
      countdown: this.countdown,
      gameMessages: this.gameMessages,
    };
  }

  deserialize(data: any): void {
    this.state = data.state || "playing";
    this.countdown = data.countdown;
    this.gameMessages = data.gameMessages;
  }
}

/**
 * Packet for player moves sent by client to server (matches original P_CLIENTMOVES)
 * Server receives this and dispatches as PlayerMovePacket (P_MOVES)
 */
export class ClientMovePacket extends Packet {
  /** Connection ID of the player making the move */
  connectionId: string = "";
  /** Player number (0-5) for packet matching - matches original p->player */
  numPlayer: number = 0;
  /** Array of moves being made */
  moves: PlayerMove[] = [];
  /** Size of moves array (matches original size uint8_t) */
  size: number = 0;
  /** Current board state checksum for validation */
  stateHash?: number;

  constructor() {
    super(PacketTypes.CLIENT_MOVE);
  }

  serialize(): any {
    return {
      connectionId: this.connectionId,
      numPlayer: this.numPlayer,
      moves: this.moves,
      size: this.size,
      stateHash: this.stateHash,
    };
  }

  deserialize(data: any): void {
    this.connectionId = data.connectionId || data.playerId || ""; // Backward compatibility
    this.numPlayer = data.numPlayer !== undefined ? data.numPlayer : (data.playerNumber || 0); // Backward compatibility
    this.moves = data.moves || [];
    this.size = data.size !== undefined ? data.size : (data.moves?.length || 0);
    this.stateHash = data.stateHash;
  }
}

/**
 * Packet for player moves dispatched by server to clients (matches original P_MOVES)
 * This is what clients receive after server processes ClientMovePacket
 */
export class PlayerMovePacket extends Packet {
  /** Connection ID of the player making the move */
  connectionId: string = "";
  /** Player number (0-5) for packet matching - matches original p->player */
  numPlayer: number = 0;
  /** Array of moves being made */
  moves: PlayerMove[] = [];
  /** Size of moves array (matches original size uint8_t) */
  size: number = 0;
  /** Current board state checksum for validation */
  stateHash?: number;

  constructor() {
    super(PacketTypes.PLAYER_MOVE);
  }

  serialize(): any {
    return {
      connectionId: this.connectionId,
      numPlayer: this.numPlayer,
      moves: this.moves,
      size: this.size,
      stateHash: this.stateHash,
    };
  }

  deserialize(data: any): void {
    this.connectionId = data.connectionId || data.playerId || ""; // Backward compatibility
    this.numPlayer = data.numPlayer !== undefined ? data.numPlayer : (data.playerNumber || 0); // Backward compatibility
    this.moves = data.moves || [];
    this.size = data.size !== undefined ? data.size : (data.moves?.length || 0);
    this.stateHash = data.stateHash;
  }
}

/**
 * Packet for line attacks sent by clients to server (matches original Packet_clientlines)
 * Server receives this and dispatches as PlayerLinesPacket (P_LINES)
 */
export class ClientLinesPacket extends Packet {
  /** Player number (0-5) for matching (matches original p->player) */
  numPlayer: number = 0;
  /** Number of lines (matches original nb uint8_t) */
  nb: number = 0;
  /** Complexity (matches original nc uint8_t) */
  nc: number = 0;
  /** Line type (matches original lx uint8_t, 4-13 or 127 for new complexity) */
  lx: number = 4;
  /** Sender player number (matches original sender uint8_t) */
  sender: number = 255;
  /** Attack details */
  attack: Attack;
  /** Hole positions for each line (matches original hole_pos[36] uint16_t) */
  holePositions: number[] = [];

  constructor() {
    super(PacketTypes.CLIENT_LINES);
    this.attack = {
      lines: 0,
      complexity: 0,
      type: "normal",
      sourceConnectionId: "",
    };
  }

  serialize(): any {
    return {
      numPlayer: this.numPlayer,
      nb: this.nb,
      nc: this.nc,
      lx: this.lx,
      sender: this.sender,
      attack: this.attack,
      holePositions: this.holePositions,
    };
  }

  deserialize(data: any): void {
    this.numPlayer = data.numPlayer !== undefined ? data.numPlayer : (data.playerNumber || 0);
    this.nb = data.nb !== undefined ? data.nb : (data.attack?.lines || 0);
    this.nc = data.nc !== undefined ? data.nc : (data.attack?.complexity || 0);
    this.lx = data.lx !== undefined ? data.lx : 4;
    this.sender = data.sender !== undefined ? data.sender : 255;
    this.attack = data.attack || {
      lines: data.nb || 0,
      complexity: data.nc || 0,
      type: "normal",
      sourceConnectionId: "",
    };
    this.holePositions = data.holePositions || data.attack?.holePositions || [];
  }
}

/**
 * Packet for line attacks dispatched by server to clients (matches original Packet_lines)
 * This is what clients receive after server processes ClientLinesPacket
 */
export class PlayerLinesPacket extends Packet {
  /** Player number (0-5) for matching (matches original p->player) */
  numPlayer: number = 0;
  /** Number of lines (matches original nb uint8_t) */
  nb: number = 0;
  /** Complexity (matches original nc uint8_t) */
  nc: number = 0;
  /** Line type (matches original lx uint8_t, 4-13 or 127 for new complexity) */
  lx: number = 4;
  /** Sender player number (matches original sender uint8_t) */
  sender: number = 255;
  /** Attack details */
  attack: Attack;
  /** Hole positions for each line (matches original hole_pos[36] uint16_t) */
  holePositions: number[] = [];

  constructor() {
    super(PacketTypes.PLAYER_LINES);
    this.attack = {
      lines: 0,
      complexity: 0,
      type: "normal",
      sourceConnectionId: "",
    };
  }

  serialize(): any {
    return {
      numPlayer: this.numPlayer,
      nb: this.nb,
      nc: this.nc,
      lx: this.lx,
      sender: this.sender,
      attack: this.attack,
      holePositions: this.holePositions,
    };
  }

  deserialize(data: any): void {
    this.numPlayer = data.numPlayer !== undefined ? data.numPlayer : (data.playerNumber || 0); // Backward compatibility
    this.nb = data.nb !== undefined ? data.nb : (data.attack?.lines || 0);
    this.nc = data.nc !== undefined ? data.nc : (data.attack?.complexity || 0);
    this.lx = data.lx !== undefined ? data.lx : 4;
    this.sender = data.sender !== undefined ? data.sender : 255;
    this.attack = data.attack || {
      lines: data.nb || 0,
      complexity: data.nc || 0,
      type: "normal",
      sourceConnectionId: "",
    };
    this.holePositions = data.holePositions || data.attack?.holePositions || [];
  }
}

/**
 * Packet for chat messages
 */
export class ChatMessagePacket extends Packet {
  /** Connection ID of the player sending the message */
  connectionId: string = "";
  /** Message content */
  message: string = "";
  /** Target team (undefined for all) */
  teamTarget?: number;
  /** Timestamp of the message */
  timestamp: number = Date.now();

  constructor() {
    super(PacketTypes.CHAT_MESSAGE);
  }

  serialize(): any {
    return {
      connectionId: this.connectionId,
      message: this.message,
      teamTarget: this.teamTarget,
      timestamp: this.timestamp,
    };
  }

  deserialize(data: any): void {
    this.connectionId = data.connectionId || data.playerId || ""; // Backward compatibility
    this.message = data.message;
    this.teamTarget = data.teamTarget;
    this.timestamp = data.timestamp || Date.now();
  }
}

/**
 * Packet for game start signal
 */
export class GameStartPacket extends Packet {
  /** Countdown in seconds before game starts */
  countdown: number = 3;
  /** Random seed for the game */
  gameSeed: number = 0;
  /** Start timestamp (server time) */
  startTime: number = 0;

  constructor() {
    super(PacketTypes.GAME_START);
  }

  serialize(): any {
    return {
      countdown: this.countdown,
      gameSeed: this.gameSeed,
      startTime: this.startTime,
    };
  }

  deserialize(data: any): void {
    this.countdown = data.countdown;
    this.gameSeed = data.gameSeed;
    this.startTime = data.startTime;
  }
}

/**
 * Packet for client pause request (matches original P_CLIENTPAUSE)
 * Reference: quadra/source/packets.h:266-271 (Packet_clientpause)
 * Sent by clients to server to request pause/unpause
 */
export class ClientPausePacket extends Packet {
  constructor() {
    super(PacketTypes.CLIENT_PAUSE);
  }

  serialize(): any {
    return {};
  }

  deserialize(data: any): void {
    // No data in P_CLIENTPAUSE
  }
}

/**
 * Packet for server pause notification (matches original P_PAUSE)
 * Reference: quadra/source/packets.h:273-281 (Packet_pause)
 * Sent by server to all clients to notify pause/unpause
 * player: -1 for server, or player number (0-5) for specific player
 */
export class GamePausePacket extends Packet {
  /** Player number who paused (-1 for server, 0-5 for specific player) */
  player: number = -1;
  /** Delay start value (500 = start countdown, 0 = immediate) */
  delayStart: number = 0;

  constructor() {
    super(PacketTypes.GAME_PAUSE);
  }

  serialize(): any {
    return {
      player: this.player,
      delayStart: this.delayStart,
    };
  }

  deserialize(data: any): void {
    this.player = data.player !== undefined ? data.player : -1;
    this.delayStart = data.delayStart || 0;
  }
}

/**
 * Packet for player state updates
 */
export class PlayerStatePacket extends Packet {
  /** Connection ID of the player being updated */
  connectionId: string = "";
  /** New state for the player */
  state: "normal" | "ghost" | "dead" | "winner" = "normal";
  /** Board update (if applicable) */
  boardUpdate?: Partial<BoardState>;
  /** Score update (if applicable) */
  scoreUpdate?: number;
  /** Lines cleared update (if applicable) */
  linesUpdate?: number;
  /** Level update (if applicable) */
  levelUpdate?: number;
  /** Frags update (if applicable) - for syncing frag counts to server */
  fragsUpdate?: number;
  /** Deaths update (if applicable) - for syncing death counts to server */
  deathsUpdate?: number;
  /** PPM update (if applicable) - for syncing PPM to server */
  ppmUpdate?: number;
  /** BPM update (if applicable) - for syncing BPM to server */
  bpmUpdate?: number;

  constructor() {
    super(PacketTypes.PLAYER_STATE);
  }

  serialize(): any {
    return {
      connectionId: this.connectionId,
      state: this.state,
      boardUpdate: this.boardUpdate,
      scoreUpdate: this.scoreUpdate,
      linesUpdate: this.linesUpdate,
      levelUpdate: this.levelUpdate,
      fragsUpdate: this.fragsUpdate,
      deathsUpdate: this.deathsUpdate,
      ppmUpdate: this.ppmUpdate,
      bpmUpdate: this.bpmUpdate,
    };
  }

  deserialize(data: any): void {
    this.connectionId = data.connectionId || data.playerId || ""; // Backward compatibility
    this.state = data.state;
    this.boardUpdate = data.boardUpdate;
    this.scoreUpdate = data.scoreUpdate;
    this.linesUpdate = data.linesUpdate;
    this.levelUpdate = data.levelUpdate;
    this.fragsUpdate = data.fragsUpdate;
    this.deathsUpdate = data.deathsUpdate;
    this.ppmUpdate = data.ppmUpdate;
    this.bpmUpdate = data.bpmUpdate;
  }
}

/**
 * Packet for per-player canvas state updates (matches original P_STATE)
 * Sent by server to notify clients of each player's CanvasStatus
 */
export class PlayerCanvasStatePacket extends Packet {
  numPlayer: number = 0;
  canvasState: "playing" | "waitForWinner" | "waitForRestart" | "last" = "playing";

  constructor() {
    super(PacketTypes.PLAYER_CANVAS_STATE);
  }

  serialize(): any {
    return {
      numPlayer: this.numPlayer,
      canvasState: this.canvasState,
    };
  }

  deserialize(data: any): void {
    this.numPlayer = data.numPlayer || 0;
    this.canvasState = data.canvasState || "playing";
  }
}

/**
 * Packet for player leaving the game
 */
export class PlayerLeavePacket extends Packet {
  /** Connection ID of the player leaving */
  connectionId: string = "";
  /** Reason for leaving */
  reason?: "quit" | "timeout" | "kicked" | "error";
  /** Whether to announce the leave in chat */
  announce: boolean = true;

  constructor() {
    super(PacketTypes.PLAYER_LEAVE);
  }

  serialize(): any {
    return {
      connectionId: this.connectionId,
      reason: this.reason,
      announce: this.announce,
    };
  }

  deserialize(data: any): void {
    this.connectionId = data.connectionId || data.playerId || ""; // Backward compatibility
    this.reason = data.reason;
    this.announce = data.announce !== undefined ? data.announce : true;
  }
}

/**
 * Packet for game over notification
 */
export class GameOverPacket extends Packet {
  /** Game results */
  results: {
    winnerConnectionId?: string;
    winnerTeam?: number;
    playerStats: {
      connectionId: string;
      stats: PlayerStats;
      rank: number;
    }[];
    gameTime: number;
  };

  constructor() {
    super(PacketTypes.GAME_OVER);
    this.results = {
      playerStats: [],
      gameTime: 0,
    };
  }

  serialize(): any {
    return this.results;
  }

  deserialize(data: any): void {
    this.results = data;
  }
}

/**
 * Packet for ping (latency measurement)
 */
export class PingPacket extends Packet {
  /** Timestamp when the ping was sent */
  timestamp: number = Date.now();
  /** Unique identifier for this ping */
  pingId: number = 0;

  constructor() {
    super(PacketTypes.PING);
  }

  serialize(): any {
    return {
      timestamp: this.timestamp,
      pingId: this.pingId,
    };
  }

  deserialize(data: any): void {
    this.timestamp = data.timestamp;
    this.pingId = data.pingId;
  }
}

/**
 * Packet for pong (response to ping)
 */
export class PongPacket extends Packet {
  /** Timestamp from the original ping */
  originalTimestamp: number = 0;
  /** ID from the original ping */
  pingId: number = 0;
  /** Server processing time in ms */
  processingTime: number = 0;

  constructor() {
    super(PacketTypes.PONG);
  }

  serialize(): any {
    return {
      originalTimestamp: this.originalTimestamp,
      pingId: this.pingId,
      processingTime: this.processingTime,
    };
  }

  deserialize(data: any): void {
    this.originalTimestamp = data.originalTimestamp;
    this.pingId = data.pingId;
    this.processingTime = data.processingTime || 0;
  }
}

/**
 * Packet for game settings updates
 */
export class GameSettingsPacket extends Packet {
  /** Updated game settings */
  settings: {
    gameMode?: string;
    levelUp?: boolean;
    startLevel?: number;
    comboMin?: number;
    allowHandicap?: boolean;
    gameEndType?: "none" | "frag" | "lines" | "time" | "score";
    gameEndValue?: number;
  } = {};

  constructor() {
    super(PacketTypes.GAME_SETTINGS);
  }

  serialize(): any {
    return this.settings;
  }

  deserialize(data: any): void {
    this.settings = data;
  }
}

/**
 * Packet sent by client when local player dies
 * Matches original: P_CLIENTDEAD
 * Original structure: only has player (numPlayer) and then_gone
 * Reference: quadra/source/packets.h:387-391 and player.cc:1139-1142
 */
export class ClientDeadPacket extends Packet {
  /** Player number (0-5) for matching (matches original p->player) */
  numPlayer: number = 0;
  /** Whether player disconnected (then_gone) */
  thenGone: boolean = false;
  /** Last attacker's player number (0-5) for frag assignment (matches original canvas->last_attacker as uint8_t) */
  lastAttackerNumPlayer?: number;

  constructor() {
    super(PacketTypes.CLIENT_DEAD);
  }

  serialize(): any {
    return {
      numPlayer: this.numPlayer,
      thenGone: this.thenGone,
      ...(this.lastAttackerNumPlayer !== undefined && { lastAttackerNumPlayer: this.lastAttackerNumPlayer }),
    };
  }

  deserialize(data: any): void {
    this.numPlayer = data.numPlayer !== undefined ? data.numPlayer : (data.playerNumber || data.player || 0); // Backward compatibility
    this.thenGone = data.thenGone || false;
    this.lastAttackerNumPlayer = data.lastAttackerNumPlayer;
  }
}

/**
 * Packet broadcast by server when a player dies
 * Matches original: P_DEAD
 * Original structure: only has player (numPlayer) and then_gone
 * Reference: quadra/source/packets.h:376-385 and net_server.cc:297-299
 */
export class DeadPacket extends Packet {
  /** Player number (0-5) for matching (matches original p->player) */
  numPlayer: number = 0;
  /** Whether player disconnected (then_gone) */
  thenGone: boolean = false;
  /** Last attacker's player number (0-5) for frag assignment (matches original canvas->last_attacker as uint8_t) */
  lastAttackerNumPlayer?: number;

  constructor() {
    super(PacketTypes.DEAD);
  }

  serialize(): any {
    return {
      numPlayer: this.numPlayer,
      thenGone: this.thenGone,
      ...(this.lastAttackerNumPlayer !== undefined && { lastAttackerNumPlayer: this.lastAttackerNumPlayer }),
    };
  }

  deserialize(data: any): void {
    this.numPlayer = data.numPlayer !== undefined ? data.numPlayer : (data.playerNumber || data.player || 0); // Backward compatibility
    this.thenGone = data.thenGone || false;
    this.lastAttackerNumPlayer = data.lastAttackerNumPlayer;
  }
}

/**
 * Packet sent by client to notify server of state change
 * Matches original: P_CLIENTSTATE
 */
export class ClientStatePacket extends Packet {
  /** Connection ID */
  connectionId: string = "";
  /** Player number (0-5) for matching (matches original Packet_state extending Packet_playerbase) */
  numPlayer: number = 0;
  /** New state */
  state: "playing" | "waitForWinner" | "waitForRestart" | "last" = "playing";

  constructor() {
    super(PacketTypes.CLIENT_STATE);
  }

  serialize(): any {
    return {
      connectionId: this.connectionId,
      numPlayer: this.numPlayer,
      state: this.state,
    };
  }

  deserialize(data: any): void {
    this.connectionId = data.connectionId || data.playerId || ""; // Backward compatibility
    this.numPlayer = data.numPlayer !== undefined ? data.numPlayer : (data.playerNumber || 0); // Backward compatibility
    this.state = data.state;
  }
}

/**
 * Packet sent by client to request respawn (matches original P_CLIENTRESPAWN)
 */
export class ClientRespawnPacket extends Packet {
  /** Connection ID */
  connectionId: string = "";
  /** Player number (0-5) for matching (matches original p->player) */
  numPlayer: number = 0;

  constructor() {
    super(PacketTypes.CLIENT_RESPAWN);
  }

  serialize(): any {
    return {
      connectionId: this.connectionId,
      numPlayer: this.numPlayer,
    };
  }

  deserialize(data: any): void {
    this.connectionId = data.connectionId || data.playerId || ""; // Backward compatibility
    this.numPlayer = data.numPlayer !== undefined ? data.numPlayer : (data.playerNumber || 0); // Backward compatibility
  }
}

/**
 * Packet broadcast by server to confirm respawn (matches original P_RESPAWN)
 */
export class RespawnPacket extends Packet {
  /** Connection ID */
  connectionId: string = "";
  /** Player number (0-5) for matching (matches original p->player) */
  numPlayer: number = 0;

  constructor() {
    super(PacketTypes.RESPAWN);
  }

  serialize(): any {
    return {
      connectionId: this.connectionId,
      numPlayer: this.numPlayer,
    };
  }

  deserialize(data: any): void {
    this.connectionId = data.connectionId || data.playerId || ""; // Backward compatibility
    this.numPlayer = data.numPlayer !== undefined ? data.numPlayer : (data.playerNumber || 0); // Backward compatibility
  }
}


/**
 * Packet for changing team
 * Sent by client to request team change
 */
export class ChangeTeamPacket extends Packet {
  /** Connection ID requesting team change */
  connectionId: string = "";
  /** New team number (0-7) */
  newTeam: number = 0;

  constructor() {
    super(PacketTypes.CHANGE_TEAM);
  }

  serialize(): any {
    return {
      connectionId: this.connectionId,
      newTeam: this.newTeam,
    };
  }

  deserialize(data: any): void {
    this.connectionId = data.connectionId || data.playerId || ""; // Backward compatibility
    this.newTeam = data.newTeam;
  }
}

/**
 * Packet for stamping a block sent by client to server (matches original P_CLIENTSTAMPBLOCK)
 * Server receives this and dispatches as StampBlockPacket (P_STAMPBLOCK)
 */
export class ClientStampBlockPacket extends Packet {
  /** Connection ID who placed the block */
  connectionId: string = "";
  /** Player number (0-5) for packet matching - matches original p->player */
  numPlayer: number = 0;
  /** X position of the block (matches original x uint8_t) */
  x: number = 0;
  /** Y position of the block (matches original y uint8_t) */
  y: number = 0;
  /** Rotation of the block (matches original rotate uint8_t) */
  rotation: number = 0;
  /** Score for this placement (matches original score uint8_t) */
  score: number = 0;
  /** Date/watch counter (matches original date uint16_t) */
  date: number = 0;
  /** Number of times block was rotated (matches original block_rotated uint8_t) */
  blockRotated: number = 0;
  /** Time block was held (matches original time_held uint16_t) */
  timeHeld: number = 0;

  constructor() {
    super(PacketTypes.CLIENT_STAMP_BLOCK);
  }

  serialize(): any {
    return {
      connectionId: this.connectionId,
      numPlayer: this.numPlayer,
      x: this.x,
      y: this.y,
      rotation: this.rotation,
      score: this.score,
      date: this.date,
      blockRotated: this.blockRotated,
      timeHeld: this.timeHeld,
    };
  }

  deserialize(data: any): void {
    this.connectionId = data.connectionId || data.playerId || ""; // Backward compatibility
    this.numPlayer = data.numPlayer !== undefined ? data.numPlayer : (data.playerNumber || 0); // Backward compatibility
    this.x = data.x;
    this.y = data.y;
    this.rotation = data.rotation;
    this.score = data.score || 0;
    this.date = data.date || 0;
    this.blockRotated = data.blockRotated || 0;
    this.timeHeld = data.timeHeld || 0;
  }
}

/**
 * Packet for stamping a block dispatched by server to clients (matches original P_STAMPBLOCK)
 * This is what clients receive after server processes ClientStampBlockPacket
 */
export class StampBlockPacket extends Packet {
  /** Connection ID who placed the block */
  connectionId: string = "";
  /** Player number (0-5) for packet matching - matches original p->player */
  numPlayer: number = 0;
  /** X position of the block (matches original x uint8_t) */
  x: number = 0;
  /** Y position of the block (matches original y uint8_t) */
  y: number = 0;
  /** Rotation of the block (matches original rotate uint8_t) */
  rotation: number = 0;
  /** Score for this placement (matches original score uint8_t) */
  score: number = 0;
  /** Date/watch counter (matches original date uint16_t) */
  date: number = 0;
  /** Number of times block was rotated (matches original block_rotated uint8_t) */
  blockRotated: number = 0;
  /** Time block was held (matches original time_held uint16_t) */
  timeHeld: number = 0;

  constructor() {
    super(PacketTypes.STAMP_BLOCK);
  }

  serialize(): any {
    return {
      connectionId: this.connectionId,
      numPlayer: this.numPlayer,
      x: this.x,
      y: this.y,
      rotation: this.rotation,
      score: this.score,
      date: this.date,
      blockRotated: this.blockRotated,
      timeHeld: this.timeHeld,
    };
  }

  deserialize(data: any): void {
    this.connectionId = data.connectionId || data.playerId || ""; // Backward compatibility
    this.numPlayer = data.numPlayer !== undefined ? data.numPlayer : (data.playerNumber || 0); // Backward compatibility
    this.x = data.x;
    this.y = data.y;
    this.rotation = data.rotation;
    this.score = data.score || 0;
    this.date = data.date || 0;
    this.blockRotated = data.blockRotated || 0;
    this.timeHeld = data.timeHeld || 0;
  }
}

/**
 * Packet for player gone/disconnected sent by client to server (matches original P_CLIENTGONE)
 * Server receives this and dispatches as GonePacket (P_GONE)
 */
export class ClientGonePacket extends Packet {
  /** Connection ID who is gone */
  connectionId: string = "";
  /** Player number (0-5) for matching (matches original p->player) */
  numPlayer: number = 0;
  /** Whether to show chat message (matches original chat_msg bool, inverted on wire) */
  chatMsg: boolean = true;

  constructor() {
    super(PacketTypes.CLIENT_GONE);
  }

  serialize(): any {
    return {
      connectionId: this.connectionId,
      numPlayer: this.numPlayer,
      chatMsg: this.chatMsg,
    };
  }

  deserialize(data: any): void {
    this.connectionId = data.connectionId || data.playerId || ""; // Backward compatibility
    this.numPlayer = data.numPlayer !== undefined ? data.numPlayer : (data.playerNumber || 0); // Backward compatibility
    this.chatMsg = data.chatMsg !== undefined ? data.chatMsg : true;
  }
}

/**
 * Packet for player gone/disconnected dispatched by server to clients (matches original P_GONE extending Packet_playerbase)
 * This is what clients receive after server processes ClientGonePacket
 */
export class GonePacket extends Packet {
  /** Connection ID who is gone */
  connectionId: string = "";
  /** Player number (0-5) for matching (matches original p->player) */
  numPlayer: number = 0;
  /** Whether to show chat message (matches original chat_msg bool, inverted on wire) */
  chatMsg: boolean = true;

  constructor() {
    super(PacketTypes.GONE);
  }

  serialize(): any {
    return {
      connectionId: this.connectionId,
      numPlayer: this.numPlayer,
      chatMsg: this.chatMsg,
    };
  }

  deserialize(data: any): void {
    this.connectionId = data.connectionId || data.playerId || ""; // Backward compatibility
    this.numPlayer = data.numPlayer !== undefined ? data.numPlayer : (data.playerNumber || 0); // Backward compatibility
    this.chatMsg = data.chatMsg !== undefined ? data.chatMsg : true;
  }
}

/**
 * Packet for removing bonus lines sent by client to server (matches original P_CLIENTREMOVEBONUS)
 * Server receives this and dispatches as RemoveBonusPacket (P_REMOVEBONUS)
 */
export class ClientRemoveBonusPacket extends Packet {
  /** Connection ID whose bonus should be removed */
  connectionId: string = "";
  /** Player number (0-5) for matching (matches original Packet_clientremovebonus extending Packet_removebonus) */
  numPlayer: number = 0;

  constructor() {
    super(PacketTypes.CLIENT_REMOVE_BONUS);
  }

  serialize(): any {
    return {
      connectionId: this.connectionId,
      numPlayer: this.numPlayer,
    };
  }

  deserialize(data: any): void {
    this.connectionId = data.connectionId || data.playerId || ""; // Backward compatibility
    this.numPlayer = data.numPlayer !== undefined ? data.numPlayer : (data.playerNumber || 0); // Backward compatibility
  }
}

/**
 * Packet for removing bonus lines dispatched by server to clients (matches original P_REMOVEBONUS extending Packet_playerbase)
 * This is what clients receive after server processes ClientRemoveBonusPacket
 */
export class RemoveBonusPacket extends Packet {
  /** Connection ID whose bonus should be removed */
  connectionId: string = "";
  /** Player number (0-5) for matching (matches original Packet_removebonus extending Packet_playerbase) */
  numPlayer: number = 0;

  constructor() {
    super(PacketTypes.REMOVE_BONUS);
  }

  serialize(): any {
    return {
      connectionId: this.connectionId,
      numPlayer: this.numPlayer,
    };
  }

  deserialize(data: any): void {
    this.connectionId = data.connectionId || data.playerId || ""; // Backward compatibility
    this.numPlayer = data.numPlayer !== undefined ? data.numPlayer : (data.playerNumber || 0); // Backward compatibility
  }
}

/**
 * Packet for first frag in survivor mode sent by client to server (matches original P_CLIENTFIRST_FRAG)
 * Server receives this and dispatches as FirstFragPacket (P_FIRST_FRAG)
 */
export class ClientFirstFragPacket extends Packet {
  /** Connection ID who got first frag */
  connectionId: string = "";
  /** Player number (0-5) for matching (matches original Packet_clientfirst_frag extending Packet_first_frag) */
  numPlayer: number = 0;

  constructor() {
    super(PacketTypes.CLIENT_FIRST_FRAG);
  }

  serialize(): any {
    return {
      connectionId: this.connectionId,
      numPlayer: this.numPlayer,
    };
  }

  deserialize(data: any): void {
    this.connectionId = data.connectionId || data.playerId || ""; // Backward compatibility
    this.numPlayer = data.numPlayer !== undefined ? data.numPlayer : (data.playerNumber || 0); // Backward compatibility
  }
}

/**
 * Packet for starting/stopping to watch a player sent by client to server (matches original P_CLIENTSTARTWATCH)
 * Server receives this and manages watchers list
 * Reference: quadra/source/packets.h:420-425 (Packet_clientstartwatch)
 */
export class ClientStartWatchPacket extends Packet {
  /** Player number (0-5) to watch (matches original Packet_clientstartwatch extending Packet_startwatch) */
  numPlayer: number = 0;
  /** Update rate (matches original Packet_startwatch::update uint8_t) */
  update?: number;
  /** Whether to stop watching (matches original Packet_startwatch::stop bool) */
  stop: boolean = false;

  constructor() {
    super(PacketTypes.CLIENT_START_WATCH);
  }

  serialize(): any {
    return {
      numPlayer: this.numPlayer,
      update: this.update,
      stop: this.stop,
    };
  }

  deserialize(data: any): void {
    this.numPlayer = data.numPlayer !== undefined ? data.numPlayer : (data.player || 0); // Backward compatibility
    this.update = data.update;
    this.stop = data.stop !== undefined ? data.stop : false;
  }
}

/**
 * Packet for first frag in survivor mode dispatched by server to clients (matches original P_FIRST_FRAG extending Packet_playerbase)
 * This is what clients receive after server processes ClientFirstFragPacket
 */
export class FirstFragPacket extends Packet {
  /** Connection ID who got first frag */
  connectionId: string = "";
  /** Player number (0-5) for matching (matches original Packet_first_frag extending Packet_playerbase) */
  numPlayer: number = 0;

  constructor() {
    super(PacketTypes.FIRST_FRAG);
  }

  serialize(): any {
    return {
      connectionId: this.connectionId,
      numPlayer: this.numPlayer,
    };
  }

  deserialize(data: any): void {
    this.connectionId = data.connectionId || data.playerId || ""; // Backward compatibility
    this.numPlayer = data.numPlayer !== undefined ? data.numPlayer : (data.playerNumber || 0); // Backward compatibility
  }
}

/**
 * Packet for game message events (frag, roundWin, etc.) sent by server to clients
 * Matches original pattern: event packets are sent immediately (not batched with state)
 * Reference: Similar to P_DEAD, P_FIRST_FRAG, P_RESPAWN - immediate event delivery
 * 
 * Unlike ServerStatePacket which batches state updates, this packet is sent
 * immediately when an event occurs to ensure real-time announcer animations.
 */
export class GameMessagePacket extends Packet {
  /** Game message event to broadcast */
  message: import("./types").GameMessageEvent;

  constructor() {
    super(PacketTypes.GAME_MESSAGE);
  }

  serialize(): any {
    return {
      message: this.message,
    };
  }

  deserialize(data: any): void {
    this.message = data.message;
  }
}

/**
 * Packet for downloading player board state (matches original P_DOWNLOAD)
 * Sent to joining players to sync game state
 * Matches Net_pendingjoin::step() lines 703-743
 */
export class DownloadStatePacket extends Packet {
  /** Player slot number (0-5) */
  player: number = 0;
  /** Board grid (10x32 visible area, matches block[y][x+4] from original) */
  board: number[][] = [];
  /** Occupied cells */
  occupied: boolean[][] = [];
  /** Blinded cells */
  blinded: number[][] = [];
  /** Random seed for this player */
  seed: number = 0;
  /** Idle state (0=busy, 1=idle, 2=dead, 3=gone) */
  idle: number = 1;
  /** Canvas state */
  canvasState: "playing" | "waitForWinner" | "waitForRestart" | "last" =
    "playing";
  /** Current piece type (255 if none) */
  currentPiece: number = 255;
  /** Next piece type (255 if none) */
  nextPiece: number = 255;
  /** Next2 piece type (255 if none) */
  nextPiece2: number = 255;
  /** Next3 piece type (255 if none) */
  nextPiece3: number = 255;
  /** Bonus lines count */
  bonus: number = 0;
  /** Bonus line data (up to 20) */
  bonusLines: Array<{
    x: number;
    color: number;
    blindTime: number;
    holePos: number;
    final: boolean;
  }> = [];
  /** Attack counts from each player (indexed by player slot) */
  attacks: number[] = [];
  /** Last attacker slot (255 if none) */
  lastAttacker: number = 255;

  constructor() {
    super(PacketTypes.DOWNLOAD_STATE);
  }

  serialize(): any {
    return {
      player: this.player,
      board: this.board,
      occupied: this.occupied,
      blinded: this.blinded,
      seed: this.seed,
      idle: this.idle,
      canvasState: this.canvasState,
      currentPiece: this.currentPiece,
      nextPiece: this.nextPiece,
      nextPiece2: this.nextPiece2,
      nextPiece3: this.nextPiece3,
      bonus: this.bonus,
      bonusLines: this.bonusLines,
      attacks: this.attacks,
      lastAttacker: this.lastAttacker,
    };
  }

  deserialize(data: any): void {
    this.player = data.player;
    this.board = data.board || [];
    this.occupied = data.occupied || [];
    this.blinded = data.blinded || [];
    this.seed = data.seed;
    this.idle = data.idle;
    this.canvasState = data.canvasState || "playing";
    this.currentPiece =
      data.currentPiece !== undefined ? data.currentPiece : 255;
    this.nextPiece = data.nextPiece !== undefined ? data.nextPiece : 255;
    this.nextPiece2 = data.nextPiece2 !== undefined ? data.nextPiece2 : 255;
    this.nextPiece3 = data.nextPiece3 !== undefined ? data.nextPiece3 : 255;
    this.bonus = data.bonus || 0;
    this.bonusLines = data.bonusLines || [];
    this.attacks = data.attacks || [];
    this.lastAttacker =
      data.lastAttacker !== undefined ? data.lastAttacker : 255;
  }
}

/**
 * Packet for player statistics (matches original P_STAT)
 * Sent to joining players to sync stats
 * Matches Net_pendingjoin::step() lines 745-754
 * 
 * Stats array format: [score, lines, frags, deaths, level, ppm, bpm]
 * - Index 0: score
 * - Index 1: lines
 * - Index 2: frags
 * - Index 3: deaths
 * - Index 4: level
 * - Index 5: ppm (Pieces Per Minute)
 * - Index 6: bpm (Blocks Per Minute)
 */
export class PlayerStatsPacket extends Packet {
  /** Player slot number (0-5) */
  player: number = 0;
  /** Statistics values: [score, lines, frags, deaths, level, ppm, bpm] */
  stats: number[] = [];

  constructor() {
    super(PacketTypes.PLAYER_STATS);
  }

  serialize(): any {
    return {
      player: this.player,
      stats: this.stats,
    };
  }

  deserialize(data: any): void {
    this.player = data.player;
    this.stats = data.stats || [];
  }
}

/**
 * Packet for player rejoin (matches original P_REJOIN)
 * Sent when a gone player rejoins the game
 * Matches net_server.cc:375-383
 */
export class RejoinPacket extends Packet {
  /** Player slot number being rejoined */
  player: number = 0;
  /** Horizontal repeat setting (matches original h_repeat int) */
  hRepeat: number = 0;
  /** Vertical repeat setting (matches original v_repeat int) */
  vRepeat: number = 0;
  /** Smooth rendering (matches original smooth int, 0 or 1) */
  smooth: number = 0;
  /** Shadow rendering (matches original shadow int, 0 or 1) */
  shadow: number = 0;
  /** Handicap level (matches original handicap int) */
  handicap: number = 0;

  constructor() {
    super(PacketTypes.REJOIN);
  }

  serialize(): any {
    return {
      player: this.player,
      hRepeat: this.hRepeat,
      vRepeat: this.vRepeat,
      smooth: this.smooth,
      shadow: this.shadow,
      handicap: this.handicap,
    };
  }

  deserialize(data: any): void {
    this.player = data.player || 0;
    this.hRepeat = data.hRepeat !== undefined ? data.hRepeat : 0;
    this.vRepeat = data.vRepeat !== undefined ? data.vRepeat : 0;
    this.smooth = data.smooth !== undefined ? (typeof data.smooth === 'boolean' ? (data.smooth ? 1 : 0) : data.smooth) : 0;
    this.shadow = data.shadow !== undefined ? (typeof data.shadow === 'boolean' ? (data.shadow ? 1 : 0) : data.shadow) : 0;
    this.handicap = data.handicap || 0;
  }
}

/**
 * Packet for server random seed (matches original P_SERVERRANDOM)
 * Reference: quadra/source/packets.h:603-612 (Packet_serverrandom)
 * Sent when round restarts to update all canvas seeds
 */
export class ServerRandomPacket extends Packet {
  /** New random seed (matches original seed uint32_t) */
  seed: number = 0;

  constructor() {
    super(PacketTypes.SERVER_RANDOM);
  }

  serialize(): any {
    return {
      seed: this.seed,
    };
  }

  deserialize(data: any): void {
    this.seed = data.seed || 0;
  }
}

/**
 * Packet for end game signal (matches original P_ENDGAME)
 * Reference: quadra/source/packets.h:531-534 (Packet_endgame)
 * Sent by server when game should end
 */
export class EndgamePacket extends Packet {
  /** Whether game ended automatically (true) or manually (false) */
  autoEnd: boolean = true;

  constructor() {
    super(PacketTypes.ENDGAME);
  }

  serialize(): any {
    return {
      autoEnd: this.autoEnd,
    };
  }

  deserialize(data: any): void {
    this.autoEnd = data.autoEnd !== undefined ? data.autoEnd : true;
  }
}

/**
 * Packet for lobby updates (team changes, game end settings)
 * Sent instead of P_GAMESERVER when only lobby state changes (not initial join)
 * Reference: Custom packet for lobby synchronization without triggering game initialization
 */
export class LobbyUpdatePacket extends Packet {
  /** All players in the game (with current teams) */
  players: Array<{
    quel: number; // Player slot (0-5)
    team: number; // Team number
    name: string; // Player name
    playerId: number; // Server-assigned player ID
    handicap: number; // Handicap level
  }> = [];
  /** Game end type (0=none, 1=frag, 2=time, 3=lines, 4=score) */
  gameEnd: number = 0;
  /** Game end value (frag limit, time limit in minutes, etc.) */
  gameEndValue: number = 0;
  /** Connection ID of the host */
  hostConnectionId: string = "";

  constructor() {
    super(PacketTypes.LOBBY_UPDATE);
  }

  serialize(): any {
    return {
      players: this.players,
      gameEnd: this.gameEnd,
      gameEndValue: this.gameEndValue,
      hostConnectionId: this.hostConnectionId,
    };
  }

  deserialize(data: any): void {
    this.players = data.players || [];
    this.gameEnd = data.gameEnd || 0;
    this.gameEndValue = data.gameEndValue || 0;
    this.hostConnectionId = data.hostConnectionId || "";
  }
}
